import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  FolderKanban,
  LayoutDashboard,
  MoreHorizontal,
  Search,
  Star,
} from 'lucide-react';

import type { Favorite, FavoriteItemType } from '@/features/favorites/api';
import {
  useFavorites,
  useRemoveFavorite,
  useReorderFavorites,
} from '@/features/favorites/hooks';
import { cn } from '@/lib/utils';

type FilterTab = 'all' | FavoriteItemType;

const TABS: { id: FilterTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'workspace', label: 'Workspaces' },
  { id: 'project', label: 'Projects' },
  { id: 'dashboard', label: 'Dashboards' },
];

const TYPE_ICON: Record<
  FavoriteItemType,
  React.ComponentType<{ className?: string }>
> = {
  workspace: Building2,
  project: FolderKanban,
  dashboard: LayoutDashboard,
};

const TYPE_LABEL: Record<FavoriteItemType, string> = {
  workspace: 'Workspace',
  project: 'Project',
  dashboard: 'Dashboard',
};

export default function FavoritesPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch, isFetching } = useFavorites();
  const remove = useRemoveFavorite();
  const reorder = useReorderFavorites();
  const [tab, setTab] = useState<FilterTab>('all');
  const [q, setQ] = useState('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  const favorites = data ?? [];
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return favorites.filter((f) => {
      if (tab !== 'all' && f.itemType !== tab) return false;
      if (!needle) return true;
      const hay = `${f.title} ${f.parentLabel ?? ''} ${TYPE_LABEL[f.itemType]}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [favorites, tab, q]);

  const allIds = favorites.map((f) => f.favoriteId);
  const pending = remove.isPending || reorder.isPending;

  const move = (favorite: Favorite, direction: -1 | 1) => {
    // Reorder within the full server list (not the filtered view).
    const next = [...allIds];
    const from = next.indexOf(favorite.favoriteId);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= next.length) return;
    const tmp = next[from];
    next[from] = next[to]!;
    next[to] = tmp!;
    setOpenMenuId(null);
    reorder.mutate(next);
  };

  return (
    <div className="mx-auto max-w-3xl p-6" data-testid="favorites-page">
      <header className="mb-6 space-y-1">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
          Favorites
        </h1>
        <p className="text-sm text-slate-500">
          Quick access to work you use often.
        </p>
      </header>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div
          className="flex flex-wrap gap-1"
          role="tablist"
          aria-label="Favorite type filters"
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              data-testid={`favorites-filter-${t.id}`}
              onClick={() => setTab(t.id)}
              className={cn(
                'rounded-full px-3 py-1.5 text-sm font-medium transition',
                tab === t.id
                  ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                  : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
        <label className="relative block w-full sm:max-w-xs">
          <span className="sr-only">Search favorites</span>
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
            aria-hidden
          />
          <input
            data-testid="favorites-search"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search favorites"
            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
          />
        </label>
      </div>

      {isLoading || (isFetching && favorites.length === 0) ? (
        <div
          className="rounded-lg border border-slate-200 px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-700"
          data-testid="favorites-page-loading"
        >
          Loading favorites…
        </div>
      ) : null}

      {isError && favorites.length === 0 ? (
        <div
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-8 text-center text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
          data-testid="favorites-page-error"
        >
          <p>Could not load favorites.</p>
          <button
            type="button"
            className="mt-2 font-medium underline"
            onClick={() => void refetch()}
          >
            Retry
          </button>
        </div>
      ) : null}

      {!isLoading && !isError && favorites.length === 0 ? (
        <div
          className="rounded-lg border border-dashed border-slate-200 px-4 py-12 text-center dark:border-slate-700"
          data-testid="favorites-page-empty"
        >
          <Star className="mx-auto h-8 w-8 text-slate-300" aria-hidden />
          <p className="mt-3 text-base font-semibold text-slate-900 dark:text-slate-100">
            No favorites yet
          </p>
          <p className="mt-1 text-sm text-slate-500">
            Add a workspace, project, or dashboard to favorites to reach it
            faster.
          </p>
        </div>
      ) : null}

      {!isLoading && favorites.length > 0 && filtered.length === 0 ? (
        <div
          className="rounded-lg border border-slate-200 px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-700"
          data-testid="favorites-page-no-match"
        >
          No favorites match this filter.
        </div>
      ) : null}

      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 dark:divide-slate-800 dark:border-slate-700">
        {filtered.map((fav) => {
          const Icon = TYPE_ICON[fav.itemType];
          const fullIndex = allIds.indexOf(fav.favoriteId);
          const isFirst = fullIndex === 0;
          const isLast = fullIndex === allIds.length - 1;
          const onlyOne = allIds.length === 1;
          const menuOpen = openMenuId === fav.favoriteId;

          return (
            <li
              key={fav.favoriteId}
              className="relative flex items-center gap-3 px-4 py-3"
              data-testid={`favorites-page-row-${fav.favoriteId}`}
            >
              <button
                type="button"
                className="flex min-w-0 flex-1 items-start gap-3 text-left"
                onClick={() => navigate(fav.href)}
              >
                <Icon
                  className="mt-0.5 h-4 w-4 shrink-0 text-slate-400"
                  aria-hidden
                />
                <span className="min-w-0">
                  <span className="block truncate font-medium text-slate-900 dark:text-slate-100">
                    {fav.title}
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-slate-500">
                    {fav.parentLabel
                      ? `${fav.parentLabel} · ${TYPE_LABEL[fav.itemType]}`
                      : TYPE_LABEL[fav.itemType]}
                  </span>
                </span>
              </button>

              <div className="relative">
                <button
                  type="button"
                  aria-label={`Actions for ${fav.title}`} title={`Actions for ${fav.title}`}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  data-testid={`favorites-page-more-${fav.favoriteId}`}
                  disabled={pending}
                  className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
                  onClick={() =>
                    setOpenMenuId(menuOpen ? null : fav.favoriteId)
                  }
                >
                  <MoreHorizontal className="h-4 w-4" aria-hidden />
                </button>
                {menuOpen ? (
                  <div
                    role="menu"
                    data-testid={`favorites-page-menu-${fav.favoriteId}`}
                    className="absolute right-0 top-full z-20 mt-1 w-48 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800"
                      onClick={() => {
                        setOpenMenuId(null);
                        navigate(fav.href);
                      }}
                    >
                      Open
                    </button>
                    {!onlyOne && !isFirst ? (
                      <button
                        type="button"
                        role="menuitem"
                        disabled={pending}
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-40 dark:hover:bg-slate-800"
                        onClick={() => move(fav, -1)}
                      >
                        Move up
                      </button>
                    ) : null}
                    {!onlyOne && !isLast ? (
                      <button
                        type="button"
                        role="menuitem"
                        disabled={pending}
                        className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50 disabled:opacity-40 dark:hover:bg-slate-800"
                        onClick={() => move(fav, 1)}
                      >
                        Move down
                      </button>
                    ) : null}
                    <button
                      type="button"
                      role="menuitem"
                      disabled={pending}
                      aria-label={`Remove ${fav.title} from favorites only — does not delete the source ${TYPE_LABEL[fav.itemType].toLowerCase()}`}
                      className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:opacity-40 dark:hover:bg-red-950/30"
                      onClick={() => {
                        setOpenMenuId(null);
                        remove.mutate({ favoriteId: fav.favoriteId });
                      }}
                    >
                      Remove from favorites
                    </button>
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
